/**
 * Spacing-prop normalizer for Puck template data.
 *
 * Template data in the wild contains FOUR generations of spacing values,
 * because the shared design system's scale changed over time:
 *
 *   1. current scale:  '0' | '1' | '2' | '4' | '6' | '8'   (Tailwind steps)
 *   2. raw numbers:    24, 32, 40…  (JSON numbers → class mb-24 etc.)
 *   3. full classes:   'mb-4', 'px-6'  (buildLayoutClasses turns these into
 *                      broken 'mb-mb-4' classes today)
 *   4. named sizes:    'sm' | 'md' | 'lg' | 'none' | '1.5rem'
 *
 * The editor's select can only display (1), so everything else shows as
 * "None" while the storefront renders whatever class the raw value spells —
 * the editor and the live site disagree (e.g. mb-24 = 6rem shown as 0).
 *
 * normalizePuckSpacing rewrites every component's marginTop/marginBottom/
 * paddingX/paddingY to the current scale (1 Tailwind step = 0.25rem),
 * dropping values it can't interpret. Applied at render time so all
 * existing templates are fixed without a data migration; values also become
 * clean the moment a merchant re-saves in the editor.
 */

const ALLOWED = ['1', '2', '4', '6', '8'] as const
const ALLOWED_STEPS = [1, 2, 4, 6, 8]

const NAMED: Record<string, string> = {
  none: '0',
  xs: '1',
  sm: '2',
  md: '4',
  lg: '6',
  xl: '8',
  '2xl': '8',
}

const SPACING_KEYS = ['marginTop', 'marginBottom', 'paddingX', 'paddingY'] as const

function nearestStep(n: number): string {
  if (n <= 0) return '0'
  if (n >= 8) return '8'
  let best = ALLOWED_STEPS[0]
  for (const s of ALLOWED_STEPS) {
    if (Math.abs(s - n) < Math.abs(best - n)) best = s // ties → smaller
  }
  return String(best)
}

export function normalizeSpacingValue(raw: unknown): string | undefined {
  if (raw === null || raw === undefined) return undefined

  let v = String(raw).trim().toLowerCase()

  // Bare numbers (string or numeric): '4', 24, '24'
  if (/^\d+(\.\d+)?$/.test(v)) return nearestStep(parseFloat(v))

  // Full class names from an older format: 'mb-4', 'px-6', 'mt-2'
  const classMatch = v.match(/^(?:mt|mb|px|py)-(.+)$/)
  if (classMatch) v = classMatch[1]
  if (/^\d+(\.\d+)?$/.test(v)) return nearestStep(parseFloat(v))

  // Named sizes
  if (v in NAMED) return NAMED[v]

  // Absolute lengths: '1.5rem', '24px'
  const rem = v.match(/^([\d.]+)rem$/)
  if (rem) return nearestStep(parseFloat(rem[1]) * 4)
  const px = v.match(/^([\d.]+)px$/)
  if (px) return nearestStep(parseFloat(px[1]) / 4)

  return undefined
}

/** Recursively normalize spacing props on every component in Puck data. */
export function normalizePuckSpacing<T>(data: T): T {
  if (!data || typeof data !== 'object') return data

  const walk = (node: any) => {
    if (Array.isArray(node)) {
      node.forEach(walk)
      return
    }
    if (!node || typeof node !== 'object') return

    if (node.props && typeof node.props === 'object' && !Array.isArray(node.props)) {
      for (const key of SPACING_KEYS) {
        if (key in node.props) {
          const normalized = normalizeSpacingValue(node.props[key])
          if (normalized === undefined) delete node.props[key]
          else if (normalized !== String(node.props[key])) node.props[key] = normalized
          else if (typeof node.props[key] !== 'string') node.props[key] = normalized
        }
      }
    }

    for (const value of Object.values(node)) {
      if (value && typeof value === 'object') walk(value)
    }
  }

  // Deep-clone once — template data is shared/cached upstream and must not
  // be mutated in place.
  const cloned = JSON.parse(JSON.stringify(data))
  walk(cloned)
  return cloned
}
